import { hasPermission } from '../../../permissions/usePermissions';
import PermissionButton, { currentCan } from '../../../permissions/PermissionButton';
import React, { useState, useMemo, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Card,
  message,
  notification,
  Button,
  Typography as AntTypography,
  Row,
  Col,
  Spin,
  Alert,
  Tag,
  Table,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Space,
  Select,
  Descriptions,
  Drawer,
  Checkbox,
  Popover
} from "antd";
import {
  useFetchBonlivById, // Fetch Bonliv header by ID
  useFetchItemsInBonliv, // Fetch items by Bonliv ID
  useUpdateBonlivStatus,
  useSaveBonliv, // If saving changes is needed
} from "../../../hooks/bonlivHooks";
import { useUsers } from "../../../hooks/userHooks";
import { useDeliveryModesWithDesc } from "../../../hooks/listsHooks";
import PreparationPrintModal from './PreparationPrintModal';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  EyeOutlined,
  PrinterOutlined,
  CheckOutlined, 
  UndoOutlined,
  ArrowLeftOutlined,
  DownOutlined,
  MinusOutlined,
  SyncOutlined
} from "@ant-design/icons";
import NestedBonlivTable from '../NestedBonlivTable';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

const { Title, Text } = AntTypography;
const { Option } = Select;

const titleStyle = { marginBottom: "4px", marginTop: "0px" };
const formatDate = (date) => {
  if (!date) return "Indisp.";
  try {
    if (typeof date === 'string' && date.includes('T')) {
      const [datePart] = date.split('T');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Indisp.";
    const day = d.getUTCDate().toString().padStart(2, '0');
    const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return "Indisp.";
  }
};

const formatDateTime = (date) => {
  if (!date) return "Indisp.";
  try {
    if (typeof date === 'string' && date.includes('T')) {
      const [datePart, timePart] = date.split('T');
      const [year, month, day] = datePart.split('-');
      const [time] = timePart.split('.');
      const [hours, minutes] = time.split(':');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return "Indisp.";
    const day = d.getUTCDate().toString().padStart(2, '0');
    const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
    const year = d.getUTCFullYear();
    const hours = d.getUTCHours().toString().padStart(2, '0');
    const minutes = d.getUTCMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (e) {
    return "Indisp.";
  }
};

// --- Reusable Editable Cell Logic ---
const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const isEditable = editable && !record?.prepare;

  const toggleEdit = () => {
    if (!isEditable) return;
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      if (handleSave) {
        handleSave({ ...record, ...values });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing && isEditable ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex}>
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24, cursor: isEditable ? 'pointer' : 'default' }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
const MemoEditableCell = React.memo(EditableCell);

// Accept props: bonlivId, onBackToList
const PreparationDetail = ({ bonlivId, onBackToList }) => {
  const navigate = useNavigate();
  const user = useSelector(state => state.user);
  const userPermissions = {
    isManager: user?.IsManager,
    canManageExpedition: user?.CanManageExpedition,
    isPreparateur: user?.PreparationPermission,
    isControleur: user?.ControlePermission,
    canManageDeliveryNote: user?.CanManageDeliveryNote
  };

  const { users, loading: loadingUsers } = useUsers();
  const { getDescByName: getDeliveryModeDesc } = useDeliveryModesWithDesc();

  const {
    bonliv: fetchedBonliv,
    loading: loadingBonliv,
    error: errorBonliv,
    refetch: refetchBonliv
  } = useFetchBonlivById(bonlivId);

  const [localBonliv, setLocalBonliv] = useState(null);
  const [controllerUser, setControllerUser] = useState(null);
  const [preparerUser, setPreparerUser] = useState(null);
  const [zoneControle, setZoneControle] = useState(null);
  
  useEffect(() => {
    if (fetchedBonliv) {
      setLocalBonliv({ 
        ...fetchedBonliv,
        nbr_colis: fetchedBonliv.nbr_colis
      });
      if (fetchedBonliv.controler_par) setControllerUser(fetchedBonliv.controler_par);
      if (fetchedBonliv.prepare_par) setPreparerUser(fetchedBonliv.prepare_par);
      if (fetchedBonliv.zonecontrole) setZoneControle(fetchedBonliv.zonecontrole);
    }
  }, [fetchedBonliv]);

  const {
    items: fetchedItems,
    loading: loadingItems,
    error: errorItems,
    refetch: refetchItems
  } = useFetchItemsInBonliv(bonlivId);

  const { handleSaveBonliv, isLoading: isSavingHook, error: saveError } = useSaveBonliv();

  const [localItems, setLocalItems] = useState([]);
  const [modifiedItemCodes, setModifiedItemCodes] = useState(new Set());
  const codeToIndexRef = useRef(new Map());
  const hasLocalChanges = modifiedItemCodes.size > 0;

  const { 
    updateStatus, 
    isLoading: isUpdatingStatus, 
    error: updateStatusError 
  } = useUpdateBonlivStatus();

  useEffect(() => {
      if (fetchedItems && Array.isArray(fetchedItems)) {
          const initializedItems = fetchedItems.map(item => ({
              ...item,
              qte_prepare: (item.qte_prepare == null || item.qte_prepare === -1) ? 0 : item.qte_prepare,
              prepare: item.prepare == null ? false : !!item.prepare,
              qte_controle: (item.qte_controle == null || item.qte_controle === -1) ? 0 : item.qte_controle,
              controle: item.controle == null ? false : !!item.controle,
              depose: item.depose == null ? false : !!item.depose,
          }));
          setLocalItems(initializedItems);
          const idxMap = new Map();
          initializedItems.forEach((it, idx) => idxMap.set(it.CODE_ART, idx));
          codeToIndexRef.current = idxMap;
          setModifiedItemCodes(new Set());
      } else if (fetchedItems === null && !loadingItems) {
          setLocalItems([]);
          codeToIndexRef.current = new Map();
          setModifiedItemCodes(new Set());
      }
  }, [fetchedItems, loadingItems]);

  const updateLocalItem = useCallback((codeArt, updates) => {
      setLocalItems(prevItems => {
          const index = codeToIndexRef.current.get(codeArt);
          if (index === undefined) {
            return prevItems.map(item => (
              item.CODE_ART === codeArt ? { ...item, ...updates } : item
            ));
          }
          const next = prevItems.slice();
          next[index] = { ...next[index], ...updates };
          return next;
      });
      setModifiedItemCodes(prevCodes => {
        const next = new Set(prevCodes);
        next.add(codeArt);
        return next;
      });
  }, []);

  const handleTogglePrepare = useCallback((record) => {
      updateLocalItem(record.CODE_ART, { prepare: !record.prepare });
  }, [updateLocalItem]);

  // Toggle only items passed in (per-etage)
  const handleToggleAllPrepare = useCallback((itemsToToggle = null) => {
    const canToggleAllPrepare = currentCan('preparation', 'update') && localBonliv?.status !== 'VALIDE' && localBonliv?.status !== 'PREPARE';
    const items = itemsToToggle || localItems;
    if (!canToggleAllPrepare || items.length === 0) return;
    const allPrepared = items.every(item => item.prepare);
    const newPrepareValue = !allPrepared;
    const codesToUpdate = new Set(items.map(item => item.CODE_ART));
    setLocalItems(prevItems => {
      const modifiedCodes = new Set();
      const updatedItems = prevItems.map(item => {
        if (codesToUpdate.has(item.CODE_ART)) {
          modifiedCodes.add(item.CODE_ART);
          return { ...item, prepare: newPrepareValue };
        }
        return item;
      });
      setModifiedItemCodes(prevCodes => {
        const next = new Set(prevCodes);
        modifiedCodes.forEach(code => next.add(code));
        return next;
      });
      return updatedItems;
    });
  }, [localItems, localBonliv?.status]);

  const handleToggleAllDepose = useCallback((itemsToToggle = null) => {
    const canToggleAllDepose = currentCan('preparation', 'update') && localBonliv?.status === 'EN_PREPARATION';
    const items = itemsToToggle || localItems;
    if (!canToggleAllDepose || items.length === 0) return;
    const allDeposed = items.every(item => item.depose);
    const newDeposeValue = !allDeposed;
    const codesToUpdate = new Set(items.map(item => item.CODE_ART));
    setLocalItems(prevItems => {
      const modifiedCodes = new Set();
      const updatedItems = prevItems.map(item => {
        if (codesToUpdate.has(item.CODE_ART)) {
          modifiedCodes.add(item.CODE_ART);
          return { ...item, depose: newDeposeValue };
        }
        return item;
      });
      setModifiedItemCodes(prevCodes => {
        const next = new Set(prevCodes);
        modifiedCodes.forEach(code => next.add(code));
        return next;
      });
      return updatedItems;
    });
  }, [localItems, localBonliv?.status]);

  // Controle: whole list toggle (as requested)
  const handleToggleAllControle = useCallback(() => {
    const canToggleAllControle = currentCan('control', 'update') && localBonliv?.status === 'PREPARE';
    if (!canToggleAllControle || localItems.length === 0) return;
    const allControlled = localItems.every(item => item.controle);
    const newControleValue = !allControlled;
    setLocalItems(prevItems => {
      const modifiedCodes = new Set();
      const updatedItems = prevItems.map(item => {
        modifiedCodes.add(item.CODE_ART);
        return { ...item, controle: newControleValue };
      });
      setModifiedItemCodes(prevCodes => {
        const next = new Set(prevCodes);
        modifiedCodes.forEach(code => next.add(code));
        return next;
      });
      return updatedItems;
    });
  }, [localItems, localBonliv?.status]);

  const handleIncrementPrepare = useCallback((record) => {
      if (!record.prepare) {
          const newQty = record.qte_prepare + 1;
          updateLocalItem(record.CODE_ART, { qte_prepare: newQty });
      }
  }, [updateLocalItem]);

  const handleDecrementPrepare = useCallback((record) => {
      if (!record.prepare && record.qte_prepare > 0) {
          const newQty = record.qte_prepare - 1;
          updateLocalItem(record.CODE_ART, { qte_prepare: newQty });
      }
  }, [updateLocalItem]);

  const handleItemSave = useCallback((updatedRow) => {
    updateLocalItem(updatedRow.CODE_ART, updatedRow);
  }, [updateLocalItem]);

  const handleToggleDepose = useCallback((record) => {
    updateLocalItem(record.CODE_ART, { depose: !record.depose });
  }, [updateLocalItem]);

  const handleToggleControle = useCallback((record) => {
    updateLocalItem(record.CODE_ART, { controle: !record.controle });
  }, [updateLocalItem]);

  const handleIncrementControle = useCallback((record) => {
    if (!record.controle) {
      const newQty = record.qte_controle + 1;
      updateLocalItem(record.CODE_ART, { qte_controle: newQty });
    }
  }, [updateLocalItem]);

  const handleDecrementControle = useCallback((record) => {
    if (!record.controle && record.qte_controle > 0) {
      const newQty = record.qte_controle - 1;
      updateLocalItem(record.CODE_ART, { qte_controle: newQty });
    }
  }, [updateLocalItem]);

  const handleControllerChange = (value) => {
    setControllerUser(value);
    setLocalBonliv(prev => ({ ...prev, controler_par: value }));
    setModifiedItemCodes(prev => new Set([...prev, 'HEADER_MODIFIED']));
  };
  const handlePreparerChange = (value) => {
    setPreparerUser(value);
    setLocalBonliv(prev => ({ ...prev, prepare_par: value }));
    setModifiedItemCodes(prev => new Set([...prev, 'HEADER_MODIFIED']));
  };
  const handleZoneControleChange = (value) => {
    const stringValue = value ? String(value) : null;
    setZoneControle(stringValue);
    setLocalBonliv(prev => ({ ...prev, zonecontrole: stringValue }));
    setModifiedItemCodes(prev => new Set([...prev, 'HEADER_MODIFIED']));
  };
  const handleNbrColisChange = (value) => {
    setLocalBonliv(prev => ({ ...prev, nbr_colis: value }));
    setModifiedItemCodes(prev => new Set([...prev, 'HEADER_MODIFIED']));
  };

  const handleSaveChanges = async () => {
    if (modifiedItemCodes.size === 0) {
        message.info("Aucune modification détectée pour les articles.");
        return;
    }
    if (!bonlivId) {
        message.error("ID Bonliv manquant. Impossible de sauvegarder.");
        return;
    }
    const itemsToSave = localItems
      .filter(item => modifiedItemCodes.has(item.CODE_ART))
      .map(item => {
          const { article, ...itemWithoutArticle } = item;
          return {
              ...itemWithoutArticle,
              qte_prepare: item.qte_prepare,
              prepare: item.prepare,
              qte_controle: item.qte_controle,
              controle: item.controle,
              depose: item.depose,
              CODE_ART: item.CODE_ART,
              REF_BL: localBonliv?.REF_BL
          };
      });

    if (itemsToSave.length === 0 && !modifiedItemCodes.has('HEADER_MODIFIED')) {
         message.warn("Aucun article modifié à sauvegarder.");
         return;
    }

    try {
      const bonlivData = { REF_BL: localBonliv.REF_BL };
      if (localBonliv.controler_par !== undefined || controllerUser !== null) {
        bonlivData.controler_par = localBonliv.controler_par || controllerUser;
      }
      if (localBonliv.prepare_par !== undefined || preparerUser !== null) {
        bonlivData.prepare_par = localBonliv.prepare_par || preparerUser;
      }
      if (localBonliv.zonecontrole !== undefined || zoneControle !== null) {
        bonlivData.zonecontrole = localBonliv.zonecontrole || zoneControle;
      }
      if (localBonliv.nbr_colis !== undefined) {
        bonlivData.nbr_colis = localBonliv.nbr_colis;
      }

      const dataToSave = { bonliv: bonlivData, items: itemsToSave };
      await handleSaveBonliv(dataToSave);

      message.success("Modifications enregistrées avec succès !");
      setModifiedItemCodes(new Set());
      refetchBonliv();
      refetchItems();

    } catch (error) {
      console.error("Failed to save Bonliv changes:", error);
      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK';
      notification.error({
          message: "Erreur de Sauvegarde",
          description: isNetworkError 
            ? "Impossible de sauvegarder. Veuillez vérifier votre connexion internet et réessayer." 
            : `Une erreur est survenue lors de la sauvegarde: ${error?.response?.data?.message || error?.message || 'Erreur inconnue'}`,
          duration: 6,
      });
    }
  };

  const handleResetChanges = () => {
      if (fetchedItems && Array.isArray(fetchedItems)) {
          const initializedItems = fetchedItems.map(item => ({
             ...item,
              qte_prepare: (item.qte_prepare == null || item.qte_prepare === -1) ? 0 : item.qte_prepare,
              prepare: item.prepare == null ? false : !!item.prepare,
              qte_controle: (item.qte_controle == null || item.qte_controle === -1) ? 0 : item.qte_controle,
              controle: item.controle == null ? false : !!item.controle,
              depose: item.depose == null ? false : !!item.depose,
          }));
          setLocalItems(initializedItems);
          const idxMap = new Map();
          initializedItems.forEach((it, idx) => idxMap.set(it.CODE_ART, idx));
          codeToIndexRef.current = idxMap;
      } else {
          setLocalItems([]);
          codeToIndexRef.current = new Map();
      }
      setModifiedItemCodes(new Set());
      message.info("Modifications annulées.");
  };

  const handleBack = onBackToList;

  const handleRefreshData = () => {
    message.info("Rafraîchissement des données...");
    refetchBonliv();
    refetchItems();
  };

  const handleSetPrepared = async () => {
      if (!bonlivId) {
          message.error("ID Bonliv manquant. Impossible de mettre à jour le statut.");
          return;
      }
      if (hasLocalChanges) {
          Modal.confirm({
              title: "Modifications non enregistrées",
              content: "Vous avez des modifications non enregistrées sur les articles. Voulez-vous les enregistrer avant de marquer comme préparé?",
              okText: "Enregistrer et Marquer",
              cancelText: "Annuler",
              onOk: async () => {
                  try {
                      await handleSaveChanges();
                      await updateStatus(bonlivId, 'PREPARE');
                      message.success("Modifications enregistrées et statut mis à jour à 'Préparé' avec succès !");
                      refetchBonliv();
                      refetchItems();
                  } catch (error) {
                      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK';
                      notification.error({
                          message: "Échec de l'opération",
                          description: isNetworkError 
                            ? "Impossible de sauvegarder. Veuillez vérifier votre connexion internet et réessayer." 
                            : `Une erreur est survenue: ${error?.response?.data?.message || error?.message || 'Erreur inconnue'}`,
                          duration: 6,
                      });
                  }
              },
              onCancel: () => {
                 message.info("Mise à jour du statut annulée. Veuillez enregistrer vos modifications.");
              }
          });
      } else {
          try {
              await updateStatus(bonlivId, 'PREPARE');
              message.success("Statut mis à jour à 'Préparé' avec succès !");
              refetchBonliv();
              refetchItems();
          } catch (error) {
              const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK';
              notification.error({
                  message: "Erreur Mise à Jour Statut",
                  description: isNetworkError 
                    ? "Impossible de mettre à jour le statut. Veuillez vérifier votre connexion internet et réessayer." 
                    : `Une erreur est survenue: ${error?.response?.data?.message || error?.message || 'Erreur inconnue'}`,
                  duration: 6,
              });
          }
      }
  };

  const handleSetControlled = async () => {
    if (!bonlivId) {
        message.error("ID Bonliv manquant. Impossible de mettre à jour le statut.");
        return;
    }
    if (hasLocalChanges) {
        Modal.confirm({
            title: "Modifications non enregistrées",
            content: "Vous avez des modifications non enregistrées sur les articles. Voulez-vous les enregistrer avant de marquer comme contrôlé?",
            okText: "Enregistrer et Marquer",
            cancelText: "Annuler",
            onOk: async () => {
                try {
                    await handleSaveChanges();
                    await updateStatus(bonlivId, 'VALIDE');
                    message.success("Modifications enregistrées et statut mis à jour à 'Contrôlé' avec succès !");
                    refetchBonliv();
                    refetchItems();
                } catch (error) {
                    const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK';
                    notification.error({
                        message: "Échec de l'opération",
                        description: isNetworkError 
                          ? "Impossible de sauvegarder. Veuillez vérifier votre connexion internet et réessayer." 
                          : `Une erreur est survenue: ${error?.response?.data?.message || error?.message || 'Erreur inconnue'}`,
                        duration: 6,
                    });
                }
            },
            onCancel: () => {
               message.info("Mise à jour du statut annulée. Veuillez enregistrer vos modifications.");
            }
        });
    } else {
        try {
            await updateStatus(bonlivId, 'VALIDE');
            message.success("Statut mis à jour à 'Contrôlé' avec succès !");
            refetchBonliv();
            refetchItems();
        } catch (error) {
            const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ERR_NETWORK';
            notification.error({
                message: "Erreur Mise à Jour Statut",
                description: isNetworkError 
                  ? "Impossible de mettre à jour le statut. Veuillez vérifier votre connexion internet et réessayer." 
                  : `Une erreur est survenue: ${error?.response?.data?.message || error?.message || 'Erreur inconnue'}`,
                duration: 6,
            });
        }
    }
  };

  const [quantityPopoverVisible, setQuantityPopoverVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState(0);
  const quantityInputRef = useRef(null);

  const isSmallDevice = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 1024;
  }, []);

  const [printModalVisible, setPrintModalVisible] = useState(false);

  const handleQuantityClick = useCallback((record, field) => {
    setEditingItem(record);
    setEditingField(field);
    setEditingQuantity(field === 'qte_prepare' ? record.qte_prepare : record.qte_controle);
    setQuantityPopoverVisible(true);
    setTimeout(() => {
      if (quantityInputRef.current) {
        quantityInputRef.current.focus();
      }
    }, 100);
  }, []);

  const handleQuantityChange = useCallback((value) => {
    setEditingQuantity(value);
  }, []);

  const handleApplyQuantity = useCallback(() => {
    if (editingItem && editingField) {
      updateLocalItem(editingItem.CODE_ART, { [editingField]: editingQuantity });
    }
    setQuantityPopoverVisible(false);
  }, [editingItem, editingField, editingQuantity]);

  const handleQuantityPopoverClose = useCallback(() => {
    setQuantityPopoverVisible(false);
  }, []);

  // --- Columns Definition ---
  // IMPORTANT: we pass the current table’s items (headerItems) so header checkboxes act only on that set
  const createColumnsWithItems = useCallback((itemsForHeader = null) => {
    const headerItems = itemsForHeader || localItems; // items for this specific table (etage) or whole list

    return (() => {
      const baseColumns = [
        { 
          title: "Code", 
          dataIndex: "CODE_ART", 
          key: "CODE_ART", 
          width: 100,
          render: (text, record) => {
            const stock = record?.article?.stocks?.[0];
            const etage = stock?.Etage ?? record?.article?.Etage ?? 'Indisp.';
            const zone = stock?.Zone ?? record?.article?.Zone ?? 'Indisp.';
            const qte_stk = stock?.qte_stk ?? record?.article?.qte_stk ?? 0;
            return (
              <div>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{text}</div>
                <div style={{ fontSize: '11px', color: '#0B0B45' }}>
                  {etage} | {zone} | 
                  <span style={{ color: qte_stk > 0 ? 'black' : 'red', marginLeft: '5px' }}>
                    Stock: {qte_stk}
                  </span>
                </div>
              </div>
            );
          }
        },
        { 
          title: "Article", 
          dataIndex: "ARTICLE", 
          key: "ARTICLE", 
          width: 500,
          render: (text, record) => (
            <div>
              <div style={{ fontSize: '13px', fontWeight: 'bold', whiteSpace: 'normal', wordBreak: 'break-word' }}>{text}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {(record.article?.REF_OEM || record.article?.q_paquet || record.article?.qt_cart) && (
                  <div>
                    {record.article?.REF_OEM && (
                      <span style={{ marginRight: '10px' }}>
                        Ref: {record.article?.REF_OEM}
                      </span>
                    )}
                    {(record.article?.q_paquet || record.article?.qt_cart) && (
                      <span style={{ marginRight: '10px' }}>
                        Cart/pqt: {record.article?.qt_cart} / {record.article?.q_paquet} 
                      </span>
                    )}
                    {(record.article?.STK_MAX > 0) && (
                      <span style={{ marginRight: '10px' }}>
                        <span style={{ color: 'red' }}>Q_M: {record.article?.STK_MAX}</span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        },
        { 
          title: "Qté", 
          dataIndex: "QTE", 
          key: "QTE", 
          width: 50, 
          align: 'center',
          render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
        },
        {
          title: "Qté Préparée",
          dataIndex: "qte_prepare",
          key: "qte_prepare",
          width: 120,
          align: 'center',
          render: (text, record) => {
            const isPrepared = record.prepare;
            const canEditQuantity = currentCan('preparation', 'update') && (!isPrepared && (localBonliv?.status === 'EN_PREPARATION' || userPermissions.isManager)
                                  && localBonliv?.status !== 'VALIDE');
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {localBonliv?.status !== 'PREPARE' && localBonliv?.status !== 'VALIDE' && (
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<MinusOutlined style={{ fontSize: '14px' }} />}
                    size="small"
                    onClick={() => handleDecrementPrepare(record)}
                    disabled={!canEditQuantity || record.qte_prepare <= 0 || localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE'}
                    style={{ marginRight: '5px' }}
                  />
                )}
                <Popover
                  content={
                    <div style={{ width: 200 }}>
                      <InputNumber
                        ref={quantityInputRef}
                        min={0}
                        value={editingQuantity}
                        onChange={handleQuantityChange}
                        style={{ width: '100%', marginBottom: 8 }}
                        autoFocus
                      />
                      <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleApplyQuantity}
                        style={{ width: '100%' }}
                      >
                        Appliquer
                      </Button>
                    </div>
                  }
                  title={`Qté cmd: ${record.QTE}`}
                  trigger="click"
                  open={quantityPopoverVisible && editingItem?.CODE_ART === record.CODE_ART && editingField === 'qte_prepare'}
                  onOpenChange={handleQuantityPopoverClose}
                  destroyTooltipOnHide
                >
                  <span 
                    style={{ 
                      fontWeight: 'bold', 
                      color: record.prepare ? 'green' : 'inherit', 
                      minWidth: '20px', 
                      fontSize: '14px',
                      cursor: canEditQuantity ? 'pointer' : 'default',
                      padding: '0 4px'
                    }}
                    onClick={(e) => {
                      if (canEditQuantity) {
                        e.stopPropagation();
                        handleQuantityClick(record, 'qte_prepare');
                      }
                    }}
                  >
                    {text ?? 0}
                  </span>
                </Popover>
                {localBonliv?.status !== 'PREPARE' && localBonliv?.status !== 'VALIDE' && (
                  <PermissionButton permissionModule="preparation" permissionAction="update"
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined style={{ fontSize: '14px' }} />}
                    size="small"
                    onClick={() => handleIncrementPrepare(record)}
                    disabled={!canEditQuantity || localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE'}
                    style={{ marginLeft: '5px' }}
                  />
                )}
              </div>
            );
          },
        },
      ];

      // Déposé (only in EN_PREPARATION)
      if (localBonliv?.status === 'EN_PREPARATION') {
        baseColumns.push({
          // FIX: Use closure over headerItems to limit to current table
          title: () => {
            const canToggleAllDepose = currentCan('preparation', 'update') && localBonliv?.status === 'EN_PREPARATION';
            const tableItems = headerItems; // items for this etage table
            const deposedCount = tableItems.filter(item => item.depose).length;
            const allDeposed = deposedCount > 0 && deposedCount === tableItems.length;
            const someDeposed = deposedCount > 0 && deposedCount < tableItems.length;
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                {currentCan('preparation', 'update') && <Checkbox
                  checked={allDeposed}
                  indeterminate={someDeposed}
                  onChange={() => handleToggleAllDepose(tableItems)}
                  disabled={!canToggleAllDepose || tableItems.length === 0}
                  style={{ transform: 'scale(1.1)' }}
                />}
                <span style={{ marginLeft: '4px' }}>Déposé</span>
              </div>
            );
          },
          dataIndex: "depose",
          key: "depose",
          width: 80,
          align: 'center',
          render: (depose, record) => {
            const canToggleDepose = currentCan('preparation', 'update') && (localBonliv?.status === 'EN_PREPARATION' || userPermissions.isManager);
            return (
              <Checkbox
                checked={!!depose}
                onChange={() => handleToggleDepose(record)}
                disabled={!canToggleDepose}
                style={{ transform: 'scale(1.2)' }}
              />
            );
          }
        });
      }

      // Préparé — header checkbox limited to current table via headerItems
      baseColumns.push({
        title: () => {
          const canToggleAllPrepare = currentCan('preparation', 'update') && localBonliv?.status !== 'VALIDE' && localBonliv?.status !== 'PREPARE';
          const tableItems = headerItems; // FIX: limit to this table
          const preparedCount = tableItems.filter(item => item.prepare).length;
          const allPrepared = preparedCount > 0 && preparedCount === tableItems.length;
          const somePrepared = preparedCount > 0 && preparedCount < tableItems.length;
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              {currentCan('preparation', 'update') && <Checkbox
                checked={allPrepared}
                indeterminate={somePrepared}
                onChange={() => handleToggleAllPrepare(tableItems)}
                disabled={!canToggleAllPrepare || tableItems.length === 0}
                style={{ transform: 'scale(1.1)' }}
              />}
              <span style={{ marginLeft: '4px' }}>Préparé</span>
            </div>
          );
        },
        dataIndex: "prepare",
        key: "prepare",
        width: 90,
        align: 'center',
        render: (prepare, record) => {
          const canTogglePrepare = currentCan('preparation', 'update') && ((localBonliv?.status === 'EN_PREPARATION' || userPermissions.isManager)
                                && localBonliv?.status !== 'VALIDE');
          return (
            <Checkbox
              checked={!!prepare}
              onChange={() => handleTogglePrepare(record)}
              disabled={!canTogglePrepare || localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE'}
              style={{ transform: 'scale(1.2)' }}
            />
          );
        }
      });

      // Contrôle (in PREPARE/VALIDE) — remains whole-table/global
      if (localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE') {
        baseColumns.push(
          {
            title: "Qté Contrôlée",
            dataIndex: "qte_controle",
            key: "qte_controle",
            width: 140,
            align: 'center',
            render: (text, record) => {
              const isControlled = record.controle;
              const canEditQuantity = currentCan('control', 'update') && (!isControlled && (localBonliv?.status === 'PREPARE' || userPermissions.isManager)
                                   && localBonliv?.status !== 'VALIDE');
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<MinusOutlined style={{ fontSize: '14px' }} />}
                    size="small"
                    onClick={() => handleDecrementControle(record)}
                    disabled={!canEditQuantity || record.qte_controle <= 0 || localBonliv?.status === 'VALIDE'}
                    style={{ marginRight: '5px' }}
                  />
                  <Popover
                    content={
                      <div style={{ width: 200 }}>
                        <InputNumber
                          ref={quantityInputRef}
                          min={0}
                          value={editingQuantity}
                          onChange={handleQuantityChange}
                          style={{ width: '100%', marginBottom: 8 }}
                          autoFocus
                        />
                        <Button
                          type="primary"
                          icon={<CheckOutlined />}
                          onClick={handleApplyQuantity}
                          style={{ width: '100%' }}
                        >
                          Appliquer
                        </Button>
                      </div>
                    }
                    title={`Qté cmd: ${record.QTE}`}
                    trigger="click"
                    open={quantityPopoverVisible && editingItem?.CODE_ART === record.CODE_ART && editingField === 'qte_controle'}
                    onOpenChange={handleQuantityPopoverClose}
                    destroyTooltipOnHide
                  >
                    <span 
                      style={{ 
                        fontWeight: 'bold', 
                        color: record.qte_controle !== record.qte_prepare ? 'red' : (record.controle ? 'green' : 'inherit'), 
                        minWidth: '20px', 
                        fontSize: '14px',
                        cursor: canEditQuantity ? 'pointer' : 'default',
                        padding: '0 4px'
                      }}
                      onClick={(e) => {
                        if (canEditQuantity) {
                          e.stopPropagation();
                          handleQuantityClick(record, 'qte_controle');
                        }
                      }}
                    >
                      {text ?? 0}
                    </span>
                  </Popover>
                  <PermissionButton permissionModule="control" permissionAction="update"
                    type="primary"
                    shape="circle"
                    icon={<PlusOutlined style={{ fontSize: '14px' }} />}
                    size="small"
                    onClick={() => handleIncrementControle(record)}
                    disabled={!canEditQuantity || localBonliv?.status === 'VALIDE'}
                    style={{ marginLeft: '5px' }}
                  />
                </div>
              );
            },
          },
          {
            title: () => {
              const canToggleAllControle = currentCan('control', 'update') && localBonliv?.status === 'PREPARE';
              const controlledCount = localItems.filter(item => item.controle).length;
              const allControlled = controlledCount > 0 && controlledCount === localItems.length;
              const someControlled = controlledCount > 0 && controlledCount < localItems.length;
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                  {currentCan('control', 'update') && <Checkbox
                    checked={allControlled}
                    indeterminate={someControlled}
                    onChange={handleToggleAllControle}
                    disabled={!canToggleAllControle || localItems.length === 0}
                    style={{ transform: 'scale(1.1)' }}
                  />}
                  <span style={{ marginLeft: '4px' }}>Contrôlé</span>
                </div>
              );
            },
            dataIndex: "controle",
            key: "controle",
            width: 90,
            align: 'center',
            render: (controle, record) => {
              const canToggleControle = currentCan('control', 'update') && ((localBonliv?.status === 'PREPARE' || userPermissions.isManager)
                                     && localBonliv?.status !== 'VALIDE');
              return (
                <Checkbox
                  checked={!!controle}
                  onChange={() => handleToggleControle(record)}
                  disabled={!canToggleControle || localBonliv?.status === 'VALIDE'}
                  style={{ transform: 'scale(1.2)' }}
                />
              );
            }
          }
        );
      }

      return baseColumns;
    })();
  }, [
    localBonliv?.status,
    userPermissions.isManager,
    user,
    localItems,
    handleDecrementPrepare,
    handleIncrementPrepare,
    handleToggleAllPrepare,
    handleToggleAllDepose,
    handleToggleAllControle,
    handleTogglePrepare,
    handleDecrementControle,
    handleIncrementControle,
    handleToggleControle,
    handleToggleDepose,
    handleQuantityClick,
    handleQuantityChange,
    handleQuantityPopoverClose,
    handleApplyQuantity,
    quantityPopoverVisible,
    editingItem,
    editingField,
    editingQuantity
  ]);

  const columns = useMemo(() => createColumnsWithItems(), [createColumnsWithItems]);

  const editableComponents = {
    body: {
      row: EditableRow,
      cell: MemoEditableCell,
    },
  };

  const editableColumns = columns.map((col) => {
      if (!col.editable) {
          return col;
      }
      return {
          ...col,
          onCell: (record) => ({
              record,
              editable: col.editable,
              dataIndex: col.dataIndex,
              title: col.title,
              handleSave: handleItemSave,
          }),
      };
  });

  const getRowClassName = (record) => {
    if (record.controle) return 'controlled-row';
    if (record.prepare) {
        return record.qte_prepare === record.QTE ? 'prepared-row-complete' : 'prepared-row-partial';
    }
    return '';
  };

  const getDescriptionItems = () => localBonliv ? [
    { key: 'refBL', label: 'Réf. BL', children: localBonliv.REF_BL, span: 1 },
    { key: 'refDev', label: 'Réf. Dev', children: localBonliv.REF_DEV || 'Indisp.', span: 1 },
    { key: 'dates', label: 'Date BL', children: <div style={{ fontSize: '12px' }}>{formatDateTime(localBonliv.DATE_BL)}</div>, span: 1 },
    {
      key: 'client',
      label: 'Client',
      children: (
        <div style={{ fontSize: '12px' }}>
          {localBonliv.CLIENT} 
          -  
          {localBonliv.client?.VILLE || 'Indisp.'} ({localBonliv.client?.COMPTE || 'Indisp.'})
          <br />
          <Text strong>Conditions:</Text> {localBonliv.client?.conditions || 'Aucune'}
        </div>
      ),
      span: 1
    },
    ...(localBonliv.status === 'EN_PREPARATION' || localBonliv.status === 'PREPARE' || localBonliv.status === 'VALIDE' ? [
      { 
        key: 'prepare_par', 
        label: 'Préparateur',
        children: (
          <Select
            style={{ width: '100%' }}
            placeholder="Préparateur"
            value={localBonliv.prepare_par || preparerUser}
            onChange={handlePreparerChange}
            loading={loadingUsers}
            disabled={localBonliv.status === 'VALIDE' || !(currentCan('preparation', 'assign') || currentCan('deliveryNotes', 'update'))}
            size="small"
          >
            {users?.filter(user => hasPermission(user, 'preparation')).map(user => (
              <Option key={user.UserID} value={user.UserID}>
                {user.Name || user.UserName}
              </Option>
            ))}
          </Select>
        ),
        span: 1 
      }
    ] : []),
    ...(localBonliv.status === 'PREPARE' || localBonliv.status === 'VALIDE' ? [
      { 
        key: 'controler_par', 
        label: 'Contrôleur',
        children: (
          <Select
            style={{ width: '100%' }}
            placeholder="Contrôleur"
            value={localBonliv.controler_par || controllerUser}
            onChange={handleControllerChange}
            loading={loadingUsers}
            disabled={localBonliv.status === 'VALIDE' || !(currentCan('control', 'assign') || currentCan('deliveryNotes', 'update'))}
            size="small"
          >
            {users?.filter(user => hasPermission(user, 'control')).map(user => (
              <Option key={user.UserID} value={user.UserID}>
                {user.Name || user.UserName}
              </Option>
            ))}
          </Select>
        ),
        span: 1 
      }
    ] : []),
    ...(localBonliv.status === 'PREPARE' || localBonliv.status === 'VALIDE' ? [
      { 
        key: 'zonecontrole', 
        label: 'Zone de Contrôle',
        children: (
          <Select
            style={{ width: '100%' }}
            placeholder="Zone (1-5)"
            value={localBonliv.zonecontrole || zoneControle}
            onChange={handleZoneControleChange}
            disabled={localBonliv.status === 'VALIDE' || !(currentCan('control', 'update') || currentCan('deliveryNotes', 'update'))}
            size="small"
          >
            {[1, 2, 3, 4, 5].map(zone => (
              <Option key={zone} value={zone}>
                Zone {zone}
              </Option>
            ))}
          </Select>
        ),
        span: 1 
      }
    ] : []),
    { key: 'modePaie', label: 'Paiement', children: <div style={{ fontSize: '12px' }}>{localBonliv.MODE_PAIE || 'Indisp.'}</div>, span: 1 },
    { 
      key: 'modeLiv', 
      label: 'Livraison', 
      children: (
        <div style={{ fontSize: '12px' }}>
          {localBonliv.MODELIV || 'Indisp.'}
          {getDeliveryModeDesc(localBonliv.MODELIV) && (
            <span style={{ color: '#666', marginLeft: '8px', fontStyle: 'italic' }}>
              ({getDeliveryModeDesc(localBonliv.MODELIV)})
            </span>
          )}
        </div>
      ), 
      span: 1 
    },
    { 
      key: 'nbr_colis', 
      label: 'Nbr Colis', 
      children: (
        <Select
          style={{ width: '100%', fontSize: '12px' }}
          placeholder="Nbr Colis"
          value={localBonliv.nbr_colis}
          onChange={handleNbrColisChange}
          disabled={localBonliv.status === 'VALIDE'}
          size="small"
          allowClear
        >
          {Array.from({ length: 99 }, (_, i) => i + 1).map(num => (
            <Option key={num} value={num}>
              {num} colis
            </Option>
          ))}
        </Select>
      ), 
      span: 1 
    },
    { key: 'remarque', label: 'Remarque', children: <div style={{ fontSize: '12px' }}>{localBonliv.REMARQUE || 'Aucune'}</div>, span: { xs: 3, sm: 2, md: 2, lg: 1 } },
  ] : [];

  const itemsByEtage = useMemo(() => {
    if (!localItems || localItems.length === 0 || localBonliv?.status !== 'EN_PREPARATION') {
      return {};
    }
    return localItems.reduce((acc, item) => {
      const stockRecord = item.article?.stocks?.[0];
      const etage = stockRecord?.Etage ?? item.article?.Etage ?? 'Non spécifié';
      if (!acc[etage]) acc[etage] = [];
      acc[etage].push(item);
      return acc;
    }, {});
  }, [localItems, localBonliv?.status]);

  const [sortOrder, setSortOrder] = useState(() => {
    const savedSortOrder = localStorage.getItem('preparationSortOrder');
    return savedSortOrder || 'zone';
  });
  useEffect(() => {
    localStorage.setItem('preparationSortOrder', sortOrder);
  }, [sortOrder]);

  const sortedItemsByEtage = useMemo(() => {
    if (localBonliv?.status !== 'EN_PREPARATION') return {};
    const sortedGroups = {};
    for (const etage in itemsByEtage) {
      sortedGroups[etage] = [...itemsByEtage[etage]].sort((a, b) => {
        if (sortOrder === 'zone') {
          const stockRecordA = a.article?.stocks?.[0];
          const stockRecordB = b.article?.stocks?.[0];
          const zoneA = stockRecordA?.Zone ?? a.article?.Zone ?? '';
          const zoneB = stockRecordB?.Zone ?? b.article?.Zone ?? '';
          if (zoneA < zoneB) return -1;
          if (zoneA > zoneB) return 1;
          return 0;
        } else {
          return a.CODE_ART.localeCompare(b.CODE_ART);
        }
      });
    }
    return sortedGroups;
  }, [itemsByEtage, sortOrder, localBonliv?.status]);

  const etages = useMemo(() => Object.keys(sortedItemsByEtage).sort(), [sortedItemsByEtage]);

  const sortedItemsForControl = useMemo(() => {
    if (!(localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE')) return [];
    const next = localItems.slice();
    next.sort((a, b) => a.CODE_ART.localeCompare(b.CODE_ART));
    return next;
  }, [localItems, localBonliv?.status]);

  const savedEtage = localStorage.getItem('activeEtage');
  const [expandedAccordion, setExpandedAccordion] = React.useState(null);

  useEffect(() => {
    if (etages.length > 0 && localBonliv?.status !== 'PREPARE') {
      const defaultEtage = etages[0];
      let etageToExpand = defaultEtage;
      if (savedEtage && savedEtage !== 'all' && etages.includes(savedEtage)) {
        etageToExpand = savedEtage;
      }
      setExpandedAccordion(etageToExpand);
    } else {
      setExpandedAccordion(null);
    }
  }, [etages, savedEtage, localBonliv?.status]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    const newExpandedState = isExpanded ? panel : false;
    setExpandedAccordion(newExpandedState);
    if (isExpanded) {
      localStorage.setItem('activeEtage', panel);
    }
  };

  const getEtagePreparationStatus = (etage) => {
    if (!sortedItemsByEtage[etage] || sortedItemsByEtage[etage].length === 0) {
      return { allPrepared: false, somePrepared: false };
    }
    const items = sortedItemsByEtage[etage];
    const preparedCount = items.filter(item => item.prepare).length;
    return {
      allPrepared: preparedCount === items.length,
      somePrepared: preparedCount > 0 && preparedCount < items.length
    };
  };

  const getEtageSignaleStatus = (etage) => {
    if (!localBonliv || !localBonliv.bonlivPerEtages) return false;
    const etageRecord = localBonliv.bonlivPerEtages.find(e => e.Etage === parseInt(etage));
    return etageRecord && etageRecord.status === 'SIGNALE';
  };

  if (loadingBonliv || (loadingItems && !localItems.length)) {
      return <Spin tip="Chargement des détails de préparation..." />;
  }
  if (errorBonliv) {
      return <Alert message="Erreur Chargement Bonliv" description={`${errorBonliv}`} type="error" showIcon />;
  }
  if (errorItems && !localItems.length) {
      return <Alert message="Erreur Chargement Articles" description={`${errorItems}`} type="error" showIcon />;
  }
  if (!localBonliv) {
      if (!loadingBonliv) {
         return <Alert message="Bonliv non trouvé ou chargement incomplet." type="warning" showIcon />;
      }
      return null;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <style>{`
        .prepared-row-complete td { background-color: #e6ffed !important; padding-top: 8px !important; padding-bottom: 8px !important; vertical-align: middle; }
        .prepared-row-partial td { background-color: #fff7e6 !important; padding-top: 8px !important; padding-bottom: 8px !important; vertical-align: middle; }
        .controlled-row td { background-color: #e6f7ff !important; padding-top: 8px !important; padding-bottom: 8px !important; vertical-align: middle; }
        .ant-table-row td { padding-top: 8px !important; padding-bottom: 8px !important; vertical-align: middle; font-size: 13px !important; }
        .ant-table-thead > tr > th { padding: 8px !important; font-size: 13px !important; }
        .ant-descriptions-item-label, .ant-descriptions-item-content { padding: 8px !important; font-size: 13px !important; }
        @media (max-width: 768px) {
          .ant-table-thead > tr > th, .ant-table-tbody > tr > td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .ant-card-head-title, .ant-card-extra { padding: 10px 0; font-size: 14px; }
          .ant-card-body { padding: 12px; }
          .ant-btn { padding: 0 8px; font-size: 12px; }
        }
      `}</style>

      <Card
        title={<span>Détail Préparation - {localBonliv?.REF_BL ?? 'Indisp.'} <Tag color="blue">{localBonliv?.status}</Tag></span>}
        bordered={false}
        extra={
          <Space size="small" wrap>
            {hasLocalChanges && (
              <Button icon={<UndoOutlined />} onClick={handleResetChanges} disabled={isSavingHook || isUpdatingStatus} size="small">
                Annuler
              </Button>
            )}
            <PermissionButton permissionAny={[["preparation", "update"], ["control", "update"], ["deliveryNotes", "update"], ["preparation", "assign"], ["control", "assign"]]} type="primary" icon={<SaveOutlined />} onClick={handleSaveChanges} loading={isSavingHook} disabled={!hasLocalChanges || isSavingHook || isUpdatingStatus} size="small">
              Enregistrer
            </PermissionButton>
            {localBonliv?.status === 'EN_PREPARATION' && (
              <PermissionButton permissionModule="preparation" permissionAction="complete"
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSetPrepared}
                loading={isUpdatingStatus}
                disabled={hasLocalChanges || isUpdatingStatus || isSavingHook}
                size="small"
              >
                Préparé
              </PermissionButton>
            )}
            {localBonliv?.status === 'PREPARE' && (
              <PermissionButton permissionModule="control" permissionAction="complete"
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleSetControlled}
                loading={isUpdatingStatus}
                disabled={
                  hasLocalChanges || 
                  isUpdatingStatus || 
                  isSavingHook ||
                  !localBonliv?.zonecontrole ||
                  !localBonliv?.controler_par ||
                  !localBonliv?.nbr_colis
                }
                size="small"
              >
                Contrôlé
              </PermissionButton>
            )}
            <PermissionButton permissionModule="control" permissionAction="print" icon={<PrinterOutlined />} onClick={() => setPrintModalVisible(true)} disabled={isSavingHook || isUpdatingStatus || !localBonliv?.nbr_colis} size="small" title="Imprimer étiquettes cartons">
              Étiquettes
            </PermissionButton>
            <Button icon={<SyncOutlined />} onClick={handleRefreshData} disabled={isSavingHook || isUpdatingStatus} size="small">
              Rafraîchir
            </Button>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack} disabled={isSavingHook || isUpdatingStatus} size="small">
              Sortir
            </Button>
          </Space>
        }
        size="small"
      >
        <Descriptions bordered size="small" items={getDescriptionItems()} column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }} />
        {errorItems && localItems.length === 0 && (
          <Alert
            message="Erreur Chargement Articles"
            description={`${errorItems}`}
            type="error"
            showIcon
            style={{marginTop: '10px'}}
          />
        )}
      </Card>

      <Card title={localBonliv?.status === 'PREPARE' ? "Articles à Contrôler" : 
                   localBonliv?.status === 'VALIDE' ? "Articles Validés" : 
                   "Articles à Préparer"} 
            style={{ marginTop: 12 }}
            size="small">
        {localItems && localItems.length > 0 ? (
          <>
            {localBonliv?.status === 'PREPARE' || localBonliv?.status === 'VALIDE' ? (
              <div>
                <div style={{ marginBottom: '8px' }}>
                  {(() => {
                    if (localBonliv?.status === 'VALIDE') {
                      return <Tag color="blue">Bon de livraison validé</Tag>;
                    }
                    const controlledCount = localItems.filter(item => item.controle).length;
                    const allControlled = controlledCount === localItems.length;
                    const someControlled = controlledCount > 0 && controlledCount < localItems.length;
                    return (
                      <Space>
                        {allControlled ? (
                          <Tag color="green">Tout contrôlé</Tag>
                        ) : someControlled ? (
                          <Tag color="orange">Partiellement contrôlé</Tag>
                        ) : (
                          <Tag color="red">Non contrôlé</Tag>
                        )}
                      </Space>
                    );
                  })()}
                </div>
                <Table
                  components={editableComponents}
                  rowClassName={getRowClassName}
                  bordered
                  dataSource={sortedItemsForControl}
                  columns={editableColumns}
                  rowKey="CODE_ART"
                  pagination={{ pageSize: isSmallDevice ? 40 : 80, showSizeChanger: false }}
                  size="small"
                  scroll={{ x: '100%' }}
                />
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text strong>Trier par:</Text>
                  <Select value={sortOrder} onChange={setSortOrder} style={{ width: 200 }} size="small">
                    <Option value="zone">Zone</Option>
                    <Option value="code_art">Code Article</Option>
                  </Select>
                </div>
                
                {etages.map(etage => (
                  <Accordion
                    key={etage}
                    expanded={expandedAccordion === etage}
                    onChange={handleAccordionChange(etage)}
                    sx={{ 
                      mb: 1,
                      borderLeft: getEtageSignaleStatus(etage) ? '4px solid #4caf50' : 'none'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<DownOutlined />}
                      aria-controls={`${etage}-content`}
                      id={`${etage}-header`}
                      sx={{ 
                        backgroundColor: getEtageSignaleStatus(etage) ? '#e6ffed' : 'inherit'
                      }}
                    >
                      <AntTypography.Text style={{ width: '33%', flexShrink: 0, fontWeight: 'bold', fontSize: '13px' }}>
                        Étage: {etage}
                      </AntTypography.Text>
                      <Space>
                        <AntTypography.Text style={{ color: 'grey', fontSize: '12px' }}>
                          {sortedItemsByEtage[etage].length} article(s)
                        </AntTypography.Text>
                        {(() => {
                          const status = getEtagePreparationStatus(etage);
                          const isSignale = getEtageSignaleStatus(etage);
                          if (isSignale) {
                            return <Tag color="green" style={{ fontWeight: 'bold', fontSize: '14px' }}>SIGNALE</Tag>;
                          } else if (status.allPrepared) {
                            return <Tag color="green">Tout préparé</Tag>;
                          } else if (status.somePrepared) {
                            return <Tag color="orange">Partiellement préparé</Tag>;
                          } else {
                            return <Tag color="red">Non préparé</Tag>;
                          }
                        })()}
                      </Space>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {expandedAccordion === etage && (() => {
                        // Pass the etage-specific items so header checkboxes only affect this table
                        const etageColumns = createColumnsWithItems(sortedItemsByEtage[etage]);
                        const etageEditableColumns = etageColumns.map((col) => {
                          if (!col.editable) return col;
                          return {
                            ...col,
                            onCell: (record) => ({
                              record,
                              editable: col.editable,
                              dataIndex: col.dataIndex,
                              title: col.title,
                              handleSave: handleItemSave,
                            }),
                          };
                        });
                        return (
                          <Table
                            components={editableComponents}
                            rowClassName={getRowClassName}
                            bordered
                            dataSource={sortedItemsByEtage[etage]}
                            columns={etageEditableColumns}
                            rowKey="CODE_ART"
                            pagination={false}
                            size="small"
                            scroll={{ x: '100%' }}
                          />
                        );
                      })()}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </div>
            )}
          </>
        ) : (
          <Alert
            message={loadingItems ? "Chargement des articles..." : (errorItems ? "Erreur Chargement Articles" : "Aucun article trouvé")}
            description={loadingItems ? "Veuillez patienter..." : (errorItems ? `${errorItems}` : "Aucun article n'est disponible pour ce bon de livraison.")}
            type={loadingItems ? "info" : (errorItems ? "error" : "info")}
            showIcon
          />
        )}
      </Card>

      <PreparationPrintModal
        visible={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        bonlivData={localBonliv}
      />
    </motion.div>
  );
};

export default PreparationDetail;
